
import Vue from "vue";
import {
  AssessmentResult,
  AssessmentResultStatus
} from "@/interfaces/candidate/canidate_assessments";
import InterviewLoading from "@/components/candidate/interviews/InterviewLoading.vue";
import AppAnnotationAssessmentRoot from "@/components/candidate/app_assessments/annotation_assessment/AppAnnotationAssessmentRoot.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  APP_ANNOTATION_ASSESSMENT_CANCELLED,
  APP_ANNOTATION_ASSESSMENT_COMPLETED,
  APP_ANNOTATION_CURRENT_QUESTION_INDEX,
  APP_ASSESSMENT_SS,
  ASSESSMENT_QUESTIONNAIRES,
  ASSESSMENT_QUESTIONNAIRES_ANNOTATION
} from "@/store/modules/candidates/constants";
import {
  APP_ASSESSMENTS,
  APP_ASSESSMENT_RESULT,
  MARK_ASSESSMENT_RESUMED
} from "@/store/modules/common/constants";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import AppAssessmentTermConditions from "@/components/AppAssessmentTermConditions.vue";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { get_random_number_between, wait_until } from "@/utils/global";
import AppResourseNotFound from "@/components/candidate/app_assessments/AppResourseNotFound.vue";
import {
  GET_COMPANY_DETAILS,
  GET_USER_DETAILS
} from "@/store/modules/auth/constants";
import store from "@/store";
import i18n from "@/i18n";
import router from "@/router";
import domtoimage from "dom-to-image-more";
import { request_assessment_resume } from "@/store/apis/recruiter";
import { REQUEST_ASSESSMENT_RESUME } from "@/store/urls/common";
import socketService from "@/services/socket.service";
import { SocketEvents } from "@/interfaces/app.interface";

export default Vue.extend({
  name: "InceptionAnnotationAssessmentIndex",
  components: {
    AppAssessmentTermConditions,
    InterviewLoading,
    AppAnnotationAssessmentRoot,
    AppResourseNotFound
  },
  data() {
    return {
      data_loading: false,
      data_error: "" as string,
      term_condition_accepted: false,
      local_assessment_completed: false,
      screen_shots: [] as string[],
      ss_random_number: 15,
      isTabHidden: false,
      is_reload: false,
      assessment_id: 0 as number,
      assessment_result: null as AssessmentResult | null
    };
  },
  async created() {
    this.ss_random_number = get_random_number_between(5, 10);
    // Window unload event
    window.addEventListener("beforeunload", this.beforeUnload, {
      passive: true
    });

    this.term_condition_accepted =
      localStorage.getItem("term_condition_accepted") === "1" ? true : false;

    if (this.term_condition_accepted) {
      this.process_term_condition(true);
    }
    // Add the event listener when the component is created
    // document.addEventListener("visibilitychange", this.handleVisibilityChange);
  },
  mounted() {
    this.check_stage();
  },
  watch: {
    screen_shots(val: string[]) {
      // If screen shots are more than 15 then send the screen shots to server
      if (val.length > this.ss_random_number) {
        const bucket_prefix =
          this.get_company_details?.storage_path?.assessments_path
            ?.split("/")
            .slice(1)
            .join("/") ?? "open/assessments/inception"; // Bucket prefix
        const assessment_id =
          this.app_annotation_assessments.questions[0].assessment_id; // Assessment id
        const file_name = `user_${this.get_user_details.id}_${assessment_id}_image.png`; // File name
        const assessment_type =
          this.get_company_details?.storage_path?.assessment_setting
            ?.candidates_annotation; // Assessment type
        // Emitting event to upload screen shots
        socketService.socket.emit(SocketEvents.UPLOAD_SCREEN_SHOTS, {
          screen_shots: val,
          user_id: this.get_user_details.id,
          assessment_id:
            this.app_annotation_assessments.questions[0].assessment_id,
          bucket_prefix,
          assessment_type: assessment_type ? assessment_type : "unknown",
          file_name,
          completed:
            this.assessment_completed || this.local_assessment_completed,
          cancelled: this.assessment_cancelled
        });
        this.screen_shots = [];
      }
    }
  },
  computed: {
    ...mapGetters("candidate", {
      app_annotation_assessments: ASSESSMENT_QUESTIONNAIRES_ANNOTATION,
      current_question_index: APP_ANNOTATION_CURRENT_QUESTION_INDEX,
      assessment_completed: APP_ANNOTATION_ASSESSMENT_COMPLETED,
      assessment_cancelled: APP_ANNOTATION_ASSESSMENT_CANCELLED
    }),
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS
    }),
    ...mapGetters("auth", {
      get_company_details: GET_COMPANY_DETAILS,
      get_user_details: GET_USER_DETAILS
    })
  },
  methods: {
    ...mapActions("candidate", {
      fetch_assessment_questionnaries: ASSESSMENT_QUESTIONNAIRES
    }),
    ...mapMutations("candidate", {
      set_app_annotation_assessments: ASSESSMENT_QUESTIONNAIRES_ANNOTATION,
      update_assessment_cancelled: APP_ANNOTATION_ASSESSMENT_CANCELLED,
      update_assessment_completed: APP_ANNOTATION_ASSESSMENT_COMPLETED,
      upload_assessment_screenshot: APP_ASSESSMENT_SS
    }),
    ...mapActions("common", {
      app_assessment_result: APP_ASSESSMENT_RESULT
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION,
      set_root_error: ROOT_ERROR
    }),
    /**
     *  Function to process term condition accepted or not accepted,
     * if accepted then init function is called to fetch assessment questionnaries
     * else user is redirected to dashboard
     * @param val boolean value to check if term condition is accepted or not
     */
    async process_term_condition(val: boolean) {
      if (!val) {
        console.log("Before push");
        this.local_assessment_completed = true;
        await this.$router.push("/candidate/dashboard");
      } else {
        this.term_condition_accepted = true;
        console.log("Before init");
        await this.init();
        // Screenshot capture
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (
            this.assessment_cancelled ||
            this.assessment_completed ||
            this.local_assessment_completed ||
            !this.term_condition_accepted
          )
            break;
          const randomNumber = get_random_number_between(8, 12);
          await wait_until(randomNumber * 1000);
          const img = await domtoimage.toPng(document.body);
          this.screen_shots.push(img);
        }
      }
    },
    async init() {
      this.data_loading = true; // Loading until data is fetched
      this.data_error = "";
      const assessment_setting =
        this.get_company_details?.storage_path?.assessment_setting
          ?.candidates_annotation;

      const assessment = this.app_assessments.find(
        (val: AppAssessments.Assessment) =>
          val.assessment_type === assessment_setting ||
          val.id == assessment_setting ||
          val.id === Number(assessment_setting)
      );

      if (!assessment) {
        this.data_error = this.$t("assessments.not-found").toString();
        this.data_loading = false;
        return;
      }

      this.assessment_id = assessment.id;
      // // If assessment already exist: return
      this.assessment_result = await this.app_assessment_result({
        assessment_id: assessment.id
      });

      if (
        this.assessment_result &&
        this.assessment_result.status !== AssessmentResultStatus.IN_PROGRESS
      ) {
        this.set_root_notification(
          `${this.$t("assessments.completed-already")}`
        );
        this.update_assessment_completed(true);
        this.local_assessment_completed = true;
        await this.$router.push("/candidate/dashboard");
        this.data_loading = false;
        return;
      }
      // Fetching assessment questionnaries
      const response = await this.fetch_assessment_questionnaries({
        assessment_id: assessment.id
      });
      // Checking if response is valid or not
      // If not valid then setting error message
      if (!response) {
        this.data_error = this.$t("assessments.failed-to-get").toString();
        this.data_loading = false;
        return;
      }
      // Sorting questions based on ref
      response.sort(
        (
          a: AppAssessments.AssessmentQuestionnaires,
          b: AppAssessments.AssessmentQuestionnaires
        ) => {
          if (
            a.question_options &&
            a.question_options.ref &&
            b.question_options &&
            b.question_options.ref
          ) {
            let fa = a.question_options.ref.toString().toLowerCase();
            let fb = b.question_options.ref.toString().toLowerCase();
            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            // Secondary sort by question_order if refs are the same
            return (
              (a.question_options.question_order || 0) -
              (b.question_options.question_order || 0)
            );
          }
          return 0;
        }
      );
      type QuestionnaireWithAnswer = AppAssessments.AssessmentQuestionnaires & {
        answer?: unknown;
        answer_type?: AppAssessments.AnswerType;
      };
      const final_response = response.map(
        (val: AppAssessments.AssessmentQuestionnaires) => {
          const row = val as QuestionnaireWithAnswer;
          let parsedAnswer: unknown = row.answer ?? null;
          if (typeof parsedAnswer === "string") {
            try {
              parsedAnswer = JSON.parse(parsedAnswer);
            } catch (e) {
              // Not a JSON string, keep as is
            }
          }
          return {
            ...val,
            answer: {
              answer_type: row.answer_type ?? AppAssessments.AnswerType.JSON,
              answer: parsedAnswer
            }
          };
        }
      );

      // Determine the initial question index
      let initialQuestionIndex = 0;
      if (localStorage.getItem("current_question")) {
        initialQuestionIndex = parseInt(
          localStorage.getItem("current_question") || "0"
        );
      } else if (this.assessment_result?.result_data?.saved_state) {
        const savedState = this.assessment_result.result_data.saved_state;
        if (
          typeof savedState.currentQuestionIndex === "number" &&
          savedState.currentQuestionIndex >= 0 &&
          savedState.currentQuestionIndex < final_response.length
        ) {
          initialQuestionIndex = savedState.currentQuestionIndex;
        }
      }

      this.set_app_annotation_assessments({
        questions: final_response,
        cancelled: false,
        completed: false,
        current_question_index: initialQuestionIndex
      });
      this.data_loading = false;
    },
    /**
     * Function to handle window unload event.
     * When user leaves (e.g. closes tab), request resume so they need admin approval again (N times).
     * @param e BeforeUnloadEvent
     */
    beforeUnload(e: BeforeUnloadEvent) {
      if (
        !this.assessment_completed &&
        !this.local_assessment_completed &&
        this.term_condition_accepted &&
        this.assessment_id
      ) {
        e.preventDefault();
        e.returnValue =
          "Your progress is saved. You will need admin approval to resume.";
        fetch(REQUEST_ASSESSMENT_RESUME(this.assessment_id), {
          method: "POST",
          keepalive: true,
          credentials: "include"
        }).catch(() => undefined);
      }
    },
    async check_stage() {
      const current_question = localStorage.getItem("current_question");
      console.log(current_question, "current_question");
      if (current_question) {
        this.set_app_annotation_assessments({
          current_question_index: parseInt(current_question)
        });
      }
    },
    async handleVisibilityChange() {
      // Check if the page visibility state is "hidden"
      if (document.visibilityState === "hidden" && !this.is_reload) {
        // User switched to another tab
        localStorage.setItem("ann_assessment_cancelled", "1"); // Mark as cancelled
        await this.$router.push("/candidate/dashboard");
      }
    }
  },
  // Guard to run before navigation leave current route
  // Navigation is BLOCKED during active assessment - candidates must complete once started
  async beforeRouteLeave(to, from, next) {
    // Allow navigation to auth routes (logout)
    if (to.path.startsWith("/auth")) {
      next();
      return;
    }

    // Allow navigation if:
    // 1. Assessment is completed
    // 2. Terms not accepted yet (hasn't started)
    // 3. Local assessment completed flag
    if (
      this.assessment_completed ||
      !this.term_condition_accepted ||
      this.local_assessment_completed
    ) {
      // Clean up localStorage on successful completion
      if (this.assessment_completed) {
        localStorage.removeItem("term_condition_accepted");
        localStorage.removeItem("current_question");
      }
      next();
      return;
    }

    // BLOCK ALL NAVIGATION - No confirmation, no cancel option
    // Assessment MUST be completed once started
    this.set_root_notification(
      "You must complete this assessment before leaving."
    );
    next(false);
  },
  async beforeRouteEnter(to, from, next) {
    const assessment_setting =
      store.getters[`auth/${GET_COMPANY_DETAILS}`].storage_path
        ?.assessment_setting?.candidates_annotation;

    // Find Assessment from store
    const annotation_assessment = store.getters[
      `common/${APP_ASSESSMENTS}`
    ].find(
      (val: AppAssessments.Assessment) =>
        val.assessment_type === assessment_setting ||
        val.id == assessment_setting ||
        val.id === Number(assessment_setting)
    );
    // Check if annotation assessment is attempted or not
    if (annotation_assessment) {
      const assessment_exist = await store.dispatch(
        `common/${APP_ASSESSMENT_RESULT}`,
        {
          assessment_id: annotation_assessment.id
        }
      );
      const status = assessment_exist?.status;

      // Completed / failed / cancelled etc. → behave as before
      if (
        assessment_exist &&
        status !== AppAssessments.AppAssessmentStatus.IN_PROGRESS
      ) {
        store.commit(
          ROOT_NOTIFICATION,
          i18n.t("assessments.completed-already")
        );
        if (router.currentRoute.name !== "candidate-dashboard") {
          await router.push("/candidate/dashboard");
        }
      } else if (
        assessment_exist &&
        status === AppAssessments.AppAssessmentStatus.IN_PROGRESS
      ) {
        const adminStatus =
          (assessment_exist as AssessmentResult).resume_admin_status ?? null;

        if (adminStatus === "APPROVED") {
          // Admin approved resume – track resume event and allow entry
          await store.dispatch(
            `common/${MARK_ASSESSMENT_RESUMED}`,
            annotation_assessment.id
          );
        } else {
          // No approval yet: create / keep a pending resume request and block entry
          try {
            await request_assessment_resume(annotation_assessment.id);
          } catch (_e) {
            // non-blocking – even if request fails, don't allow direct resume
          }
          store.commit(
            ROOT_NOTIFICATION,
            i18n.t("assessments.resume-awaiting-approval")
          );
          if (router.currentRoute.name !== "candidate-dashboard") {
            await router.push("/candidate/dashboard");
          }
        }
      }
    }
    next();
  },
  beforeDestroy() {
    // Remove the event listener when the component is destroyed

    // document.removeEventListener(
    //   "visibilitychange",
    //   this.handleVisibilityChange
    // );

    localStorage.removeItem("ann_assessment_cancelled");
    localStorage.removeItem("term_condition_accepted");
    localStorage.removeItem("current_question");
    localStorage.removeItem("assessment_cancelled");
  }
});
